"use client";

import { useState, useEffect, useMemo, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import Link from "next/link";
import Image from "next/image";
import IMAGES, { BASE_URL_CITY } from "@/data/assetData";
import { truncateContent } from "@/data/staticMethod";

// Blogs now arrive as a server-fetched `blogLists` prop (see ExplorerSection), so
// every blog card + /blog/<slug>/ internal link is in the initial HTML and is
// crawlable. This component only enhances them into an Embla carousel on the
// client — it no longer fetches anything.
const ExplorerSlider = ({ blogLists = [] }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const imageRegex = useMemo(() => /\.(png|jpg|jpeg)$/i, []);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: false,
      align: "start",
      slidesToScroll: 1,
      containScroll: "trimSnaps",
    },
    // 6000ms, not 2000ms: every slide is a full-card <Link> to a blog post, so a
    // 2s cadence slid the tap target away mid-reach. 6s leaves time to read a
    // title before the card moves. stopOnMouseEnter freezes it on hover/focus;
    // stopOnInteraction still halts it permanently after the first touch.
    [Autoplay({ delay: 6000, stopOnInteraction: true, stopOnMouseEnter: true })]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  const onReInit = useCallback(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    onSelect();
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi) return undefined;
    // Defer the initial snap read to the next frame so we never set state
    // synchronously inside the effect body.
    const raf = requestAnimationFrame(onReInit);
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onReInit);
    // Respect prefers-reduced-motion: halt autoplay for those users.
    const autoplay = emblaApi.plugins()?.autoplay;
    if (
      autoplay &&
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      autoplay.stop();
    }
    return () => {
      cancelAnimationFrame(raf);
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onReInit);
    };
  }, [emblaApi, onSelect, onReInit]);

  if (!blogLists || blogLists.length === 0) return null;

  const dotsToShow = Math.min(scrollSnaps.length, 3);

  return (
    <div className="mb-10">
      <div className="explorer-carousel flex-1 mx-[5%] md960:mx-[4%] pb-[30px]">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex -ml-[5px] md960:-ml-[15px]">
            {blogLists.map((item) => {
              const blogLink = `${BASE_URL_CITY}/blog/${item.url}/`;
              return (
                <div
                  key={item.url}
                  className="min-w-0 flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_25%] pl-[5px] md960:pl-[15px]"
                >
                  <Link href={blogLink} passHref>
                    <div className="relative w-full h-[250px] rounded-card overflow-hidden">
                      <Image
                        src={imageRegex.test(item.metaImageUrl) ? item.metaImageUrl : IMAGES.blogDefaultImg}
                        className="object-cover"
                        fill
                        sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 25vw"
                        alt={item.title}
                        quality={75}
                        loading="lazy"
                      />
                    </div>
                    <h3 className="text-base font-semibold text-[var(--color-text-primary)] leading-[1.4] my-2">{item.title}</h3>
                    <p
                      className="text-sm font-normal text-[var(--color-text-secondary)] leading-normal m-0 line-clamp-3"
                      dangerouslySetInnerHTML={{
                        __html: truncateContent(item.metaDesc, 150),
                      }}
                    />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>

        {/* Dots — max 3 shown */}
        <div className="flex justify-center gap-2 mt-2.5">
          {scrollSnaps.slice(0, dotsToShow).map((_, index) => (
            <button
              key={index}
              type="button"
              className="relative w-6 h-6 flex items-center justify-center cursor-pointer bg-transparent"
              onClick={() => emblaApi?.scrollTo(index)}
              aria-label={`Go to slide ${index + 1}`}
            >
              <span className={`block w-2.5 h-2.5 rounded-full ${
                index === selectedIndex
                  ? "bg-[var(--color-accent-cyan)]"
                  : "bg-[var(--color-text-muted)]"
              }`} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExplorerSlider;
